import type { BuildStats } from "@edcc/game-data";
import type { CalculatorMetricGroup, ThermalLoadMetric } from "../lib/edsy-calculator.ts";

interface ShipStatsHudProps {
  shipName: string;
  buildStats: BuildStats | null;
  groups: CalculatorMetricGroup[];
  rawStats?: Record<string, unknown> | null;
  thermalLoads?: ThermalLoadMetric[];
  note?: string | null;
}

function hasMetricValue(value: string | null | undefined): boolean {
  if (!value?.trim()) return false;
  return !["N/A", "—", "-", "ERR"].includes(value.trim().toUpperCase());
}

function numericValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = value.replace(/,/g, "").match(/-?(?:\d+\.?\d*|\.\d+)/);
  if (!match) return null;
  const number = Number(match[0]);
  return Number.isFinite(number) ? number : null;
}

function metricCell(group: CalculatorMetricGroup, rowIndex: number, columnIndex: number): string {
  const row = group.rows[rowIndex];
  if (!row) return "—";
  return row.cells?.[columnIndex] ?? (columnIndex === 0 ? row.value : "—");
}

function visibleGroups(groups: CalculatorMetricGroup[]): CalculatorMetricGroup[] {
  const visible: CalculatorMetricGroup[] = [];
  for (const group of groups) {
    if (!group.columns) {
      const rows = group.rows.filter((row) => hasMetricValue(row.value));
      if (rows.length) visible.push({ ...group, rows });
      continue;
    }

    const columns = group.columns;
    const visibleColumns = columns.flatMap((column, columnIndex) => {
      const values = group.rows.map((_, rowIndex) => metricCell(group, rowIndex, columnIndex));
      if (group.title === "TTL" && (column === "CRGO" || column === "PSGR")) {
        const capacityRowIndex = group.rows.findIndex((row) => row.label.toUpperCase() === "MAX");
        const capacity = capacityRowIndex >= 0
          ? numericValue(metricCell(group, capacityRowIndex, columnIndex))
          : null;
        return (capacity ?? 0) > 0 ? [{ index: columnIndex, name: column }] : [];
      }
      return values.some(hasMetricValue) ? [{ index: columnIndex, name: column }] : [];
    });
    if (!visibleColumns.length) continue;

    const rows = group.rows
      .map((row, rowIndex) => ({
        ...row,
        cells: visibleColumns.map(({ index }) => metricCell(group, rowIndex, index)),
      }))
      .filter((row) => row.cells?.some(hasMetricValue));
    if (!rows.length) continue;

    visible.push({
      ...group,
      columns: visibleColumns.map(({ name }) => name),
      rows,
    });
  }
  return visible;
}

function incompleteBuildGroups(stats: BuildStats | null): CalculatorMetricGroup[] {
  const number = (value: number | null | undefined, digits = 1) =>
    value === null || value === undefined ? "N/A" : value.toFixed(digits);
  const percent = (value: number | null) =>
    value === null ? "N/A" : `${(value * 100).toFixed(1)}%`;
  const rows = (items: Array<[string, string[]]>) =>
    items.map(([label, cells]) => ({ label, value: cells[0] ?? "N/A", cells }));
  const cost = stats?.price ?? null;

  return [
    {
      title: "TTL",
      columns: ["MASS", "FUEL", "CRGO", "PSGR"],
      rows: rows([
        ["CUR", [number(stats?.currentMass), number(stats?.fuelLoad), number(stats?.cargoLoad), "N/A"]],
        ["MAX", [number(stats?.maxMass), number(stats?.fuelCapacity), number(stats?.cargoCapacity), "N/A"]],
        ["CRW", ["N/A", "N/A", "N/A", "N/A"]],
        ["DST", ["N/A", "N/A", "N/A", "N/A"]],
      ]),
    },
    {
      title: "NAV",
      columns: ["JMP", "RNG", "SPD", "BST", "FRQ"],
      rows: rows([
        ["CUR", ["N/A", "N/A", number(stats?.speed, 0), number(stats?.boost, 0), "N/A"]],
        ["LDN", [number(stats?.jumpLaden, 2), number(stats?.rangeLaden, 2), "N/A", "N/A", "N/A"]],
        ["UNL", [number(stats?.jumpUnladen, 2), number(stats?.rangeUnladen, 2), "N/A", "N/A", "N/A"]],
        ["MAX", [number(stats?.jumpMax, 2), "N/A", "N/A", "N/A", "N/A"]],
        ["SCP", ["N/A", "N/A", "N/A", "N/A", "N/A"]],
      ]),
    },
    {
      title: "HND",
      columns: ["PIT", "ROL", "YAW"],
      rows: rows([
        ["CUR", ["N/A", "N/A", "N/A"]],
        ["180°", ["N/A", "N/A", "N/A"]],
        ["MAX", ["N/A", "N/A", "N/A"]],
        ["180°", ["N/A", "N/A", "N/A"]],
        ["BST", ["N/A", "N/A", "N/A"]],
      ]),
    },
    {
      title: "THM",
      columns: ["LVL"],
      rows: rows([
        ["IDL", ["N/A"]],
        ["THR", ["N/A"]],
        ["FSD", ["N/A"]],
        ["WEP", ["N/A"]],
        ["SCB", ["N/A"]],
      ]),
    },
    {
      title: "SHD",
      columns: ["RES", "STR", "RNF"],
      rows: rows([
        ["RAW", ["N/A", number(stats?.shield), "N/A"]],
        ["ABS", ["N/A", "N/A", "N/A"]],
        ["KIN", ["N/A", "N/A", "N/A"]],
        ["THM", ["N/A", "N/A", "N/A"]],
        ["EXP", ["N/A", "N/A", "N/A"]],
        ["BLD", ["N/A", "N/A", "N/A"]],
        ["RGN", ["N/A", "N/A", "N/A"]],
      ]),
    },
    {
      title: "ARM",
      columns: ["RES", "INT"],
      rows: rows([
        ["RAW", ["N/A", number(stats?.armour)]],
        ["MRP", ["N/A", "N/A"]],
        ["KIN", [percent(stats?.armourKineticResistance ?? null), "N/A"]],
        ["THM", [percent(stats?.armourThermalResistance ?? null), "N/A"]],
        ["EXP", [percent(stats?.armourExplosiveResistance ?? null), "N/A"]],
        ["CAU", [percent(stats?.armourCausticResistance ?? null), "N/A"]],
      ]),
    },
    {
      title: "WPN",
      columns: ["BST", "DUR"],
      rows: rows(["RAW", "ABS", "KIN", "THM", "EXP", "A-X", "CAU"].map((label) => [label, ["N/A", "N/A"]])),
    },
    {
      title: "PRC",
      columns: ["COST"],
      rows: rows([
        ["RFL", ["N/A"]],
        ["RPL", ["N/A"]],
        ["RSK", ["N/A"]],
        ["RAR", ["N/A"]],
        ["RBY", [cost === null ? "N/A" : number(cost * 0.05, 0)]],
        ["BUY", [number(cost, 0)]],
      ]),
    },
  ];
}

function priorityDraws(stats: Record<string, unknown> | null | undefined, key: string): number[] {
  const raw = stats?.[key];
  if (!Array.isArray(raw)) return [];
  return raw.slice(1).map((value) =>
    typeof value === "number" && Number.isFinite(value) ? value : 0,
  );
}

function PowerBar(props: {
  label: "RET" | "DEP";
  draw: number | null;
  capacity: number | null;
  priorities: number[];
}) {
  const { label, draw, capacity, priorities } = props;
  const hasScale = draw !== null && capacity !== null && capacity > 0;
  const percentage = hasScale ? (draw / capacity) * 100 : null;
  const meterValue = percentage === null ? 0 : Math.min(100, Math.max(0, percentage));
  const segments = priorities.length > 0 ? priorities : draw === null ? [] : [draw];

  return (
    <div className={`ship-stat-power-meter${percentage !== null && percentage > 100 ? " overloaded" : ""}`}>
      <div className="ship-stat-power-label">
        <strong>{label}</strong>
        <span>
          {draw === null
            ? "—"
            : `${draw.toFixed(2)} / ${capacity?.toFixed(2) ?? "—"} MW`}
        </span>
        {percentage !== null ? <small>{percentage.toFixed(1)}%</small> : null}
      </div>
      {hasScale && percentage !== null ? (
        <div
          className="ship-stat-meter"
          role="meter"
          aria-label={`${label === "RET" ? "Retracted" : "Deployed"} power draw`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={meterValue}
          aria-valuetext={`${percentage.toFixed(1)} percent of power plant output`}
          title={`${percentage.toFixed(1)}% of power plant output`}
        >
          {segments.map((value, index) => {
            const width = Math.min(100, Math.max(0, (value / capacity) * 100));
            return (
              <span
                className="ship-stat-meter-segment"
                key={`${label}-${index}`}
                style={{ width: `${width}%` }}
                title={`Priority ${index + 1}: ${value.toFixed(2)} MW`}
              >{value > 0 ? index + 1 : null}</span>
            );
          })}
        </div>
      ) : (
        <span className="ship-stat-meter-unavailable">Power data unavailable</span>
      )}
    </div>
  );
}

export function ShipStatsHud(props: ShipStatsHudProps) {
  const groups = visibleGroups(props.groups.some((group) => group.title === "TTL")
    ? props.groups
    : incompleteBuildGroups(props.buildStats));
  const retracted = props.buildStats?.powerRetracted ?? null;
  const deployed = props.buildStats?.powerDraw ?? null;
  const powerCapacity = props.buildStats?.powerCapacity ?? null;
  const hasPowerReadings = powerCapacity !== null && powerCapacity > 0;

  if (!groups.length && !hasPowerReadings && !props.note) return null;

  return (
    <section className="ship-stat-dock" aria-label="Detailed ship statistics">
      <div className="ship-stat-dock-heading">
        <strong>Detailed stats</strong>
        <span>{props.shipName}</span>
      </div>
      {props.note ? <p className="ship-stat-dock-note" role="status">{props.note}</p> : null}
      {groups.length ? (
        <div className="ship-stat-dock-groups">
          {groups.map((group) => (
            <section className="ship-stat-dock-group" key={group.title}>
              <h3>{group.title}</h3>
              {group.columns ? (
                <table>
                  <thead>
                    <tr>
                      <th scope="col" aria-label="State" />
                      {group.columns.map((column, index) => (
                        <th scope="col" key={`${column}-${index}`}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row, rowIndex) => (
                      <tr key={`${row.label}-${rowIndex}`}>
                        <th scope="row">{row.label}</th>
                        {group.columns?.map((column, index) => {
                          const value = row.cells?.[index];
                          return (
                            <td key={`${row.label}-${column}-${index}`}>
                              {hasMetricValue(value) ? value : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <dl>
                  {group.rows.map((row) => (
                    <div key={row.label}><dt>{row.label}</dt><dd>{row.value}</dd></div>
                  ))}
                </dl>
              )}
              {group.title === "TTL" && hasPowerReadings ? (
                <div className="ship-stat-power-bars" aria-label="Power draw by hardpoint state">
                  <PowerBar label="RET" draw={retracted} capacity={powerCapacity} priorities={priorityDraws(props.rawStats, "pwrdraw_ret")} />
                  <PowerBar label="DEP" draw={deployed} capacity={powerCapacity} priorities={priorityDraws(props.rawStats, "pwrdraw_dep")} />
                </div>
              ) : null}
            </section>
          ))}
        </div>
      ) : null}
    </section>
  );
}
